import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {UserDefinedWidget} from '@models/pages/user-defined-page/user-defined-widget';
import {GeocodingService} from '@services/administration/machine/geocoding/geocoding.service';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {UserDefinedWidgetType} from '@constants/pages/user-defined-page/user-defined-widget-type';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {
  DEFAULT_MAX_ZOOM_LEVEL,
  DEFAULT_MIN_ZOOM_LEVEL, DEFAULT_ZOOM_LEVEL,
  GOOGLE_MAPS_DIRECTION_URL
} from '@constants/google-maps/google-maps.constant';
import {GoogleMap, MapInfoWindow, MapMarker} from '@angular/google-maps';
import {WidgetHelperService} from '@services/pages/widget/widget-helper.service';
import {Machine} from '@models/machine/machine';
import {MapTypeId} from '@constants/google-maps/map-type-id.enum';

@Component({
    selector: 'scitis-map-widget-configuration',
    templateUrl: './map-widget-configuration.component.html',
    styleUrls: ['./map-widget-configuration.component.scss'],
    standalone: false
})
export class MapWidgetConfigurationComponent implements OnInit, OnDestroy {

  // decorators
  @Input()
  userDefinedWidget: UserDefinedWidget = new UserDefinedWidget();
  @Input()
  machine: Machine = new Machine();
  @Output()
  isConfigurationValid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('googleMap')
  public map: GoogleMap | undefined;

  // public
  public googleMapsLoaded: boolean = false;
  public mapOptions!: google.maps.MapOptions;
  public marker!: google.maps.Marker;
  public widgetForm: UntypedFormGroup = new UntypedFormGroup({});
  public mapTypeId: typeof MapTypeId | typeof google.maps.MapTypeId = MapTypeId;

  // private
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private geocodingService: GeocodingService,
    private fb: UntypedFormBuilder,
    private widgetService: WidgetHelperService,
  ) {}

  //getters
  public get f() {
    return this.widgetForm.controls;
  }

  public get isMapWidget(): boolean {
    return this.userDefinedWidget.widgetDetails.widgetType === UserDefinedWidgetType.MAP;
  }

  ngOnInit(): void {
    this.initForm();
    this.initMap();
  }

  //public methods
  public replaceInvalidValue(e): void {
    if (!+e.target.value) {
      this.f.zoomLvl.setValue(DEFAULT_MIN_ZOOM_LEVEL);
    }
  }

  public getMaxMinZoom(zoomType: 'min' | 'max'): number {
    return zoomType === 'max' ? DEFAULT_MAX_ZOOM_LEVEL : DEFAULT_MIN_ZOOM_LEVEL;
  }

  public onZoomChanged(): void {
    if (!(this.map?.getZoom() as number <= DEFAULT_MIN_ZOOM_LEVEL)) {
      this.f.zoomLvl.setValue(this.map?.getZoom());
    }
  }

  public onWheel($event: WheelEvent): void {
    if (this.map) {
      $event.preventDefault();
      const isZoomIn = $event.deltaY < 0;
      const zoom = this.map.getZoom() || 0;
      if (isZoomIn && zoom < DEFAULT_MAX_ZOOM_LEVEL) {
        this.f.zoomLvl.setValue(zoom + 1);
      }
      if (!isZoomIn && zoom > DEFAULT_MIN_ZOOM_LEVEL) {
        this.f.zoomLvl.setValue(zoom - 1);
      }
    }
  }

  public onOpenInfoWindow(mapMarker: MapMarker, infoWindow: MapInfoWindow): void {
    infoWindow.open(mapMarker);
  }

  public onCloseInfoWindow(): void {
    if (this.map) {
      this.map.panTo(this.marker.getPosition() as google.maps.LatLng);
    }
  }

  public machineAddress(): string {
    return this.widgetService.getMachineAddress(this.machine.locationDetails);
  }

  public redirectToGoogleMaps(): void {
    const {lat, lng} = this.marker.getPosition() as google.maps.LatLng;
    const url = GOOGLE_MAPS_DIRECTION_URL + `${lat()},${lng()}`;
    window.open(url, '_blank');
  }

  //private methods
  private initForm(): void {
    const {mapWidgetZoomLevel, mapWidgetMapStyle} = this.userDefinedWidget?.userDefinedChartDetails;
    this.widgetForm = this.fb.group({
      zoomLvl: [{value: mapWidgetZoomLevel ? mapWidgetZoomLevel : DEFAULT_ZOOM_LEVEL, disabled: true},
        [Validators.min(DEFAULT_MIN_ZOOM_LEVEL), Validators.max(DEFAULT_MAX_ZOOM_LEVEL), Validators.required]],
      mapType: [{value: mapWidgetMapStyle ? mapWidgetMapStyle : MapTypeId.ROADMAP, disabled: true}],
    });

    this.widgetForm.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(300)).subscribe(value => {
      if (this.googleMapsLoaded) {
        this.marker.setOptions(this.widgetService.setMarkerOptions(value.mapType));
        this.isConfigurationValid.emit(this.widgetForm.valid && this.googleMapsLoaded);
        this.userDefinedWidget.userDefinedChartDetails.mapWidgetZoomLevel = value.zoomLvl;
        this.userDefinedWidget.userDefinedChartDetails.mapWidgetMapStyle = value.mapType;
      }
    });
  }

  private initMap(): void {
    this.geocodingService.loadMapsApiObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.googleMapsLoaded = true;
        this.mapTypeId = google.maps.MapTypeId;
        this.f.mapType.enable();
        this.f.zoomLvl.enable();
        if (this.machine.locationDetails.latitude && this.machine.locationDetails.longitude) {
          const {longitude, latitude} = this.machine.locationDetails;
          const position = new google.maps.LatLng(latitude, longitude);
          this.marker = this.widgetService.markerInit(position, this.machine.type);
          this.marker.set('options', this.widgetService.setMarkerOptions(this.f.mapType.value));
          this.mapOptions = this.widgetService.mapOptionsInit(position);
        }
        this.isConfigurationValid.emit(this.widgetForm.valid && this.googleMapsLoaded);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
